import config from '../config';
import {
  LoginRequest,
  LoginResponse,
  Company,
  User,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CreateUserRequest,
  UpdateUserRequest,
  VerifyUserRequest,
  TestSuiteResponse,
  CreateTestSuiteRequest,
  CreateTestSuiteResponse,
  TestDetails,
  UpdateTestRequest,
  UpdateTestResponse,
  UploadReceiptRequest,
  UploadReceiptResponse,
  Document,
  CreateDocumentRequest,
  Notification,
  CertificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SendEmailRequest,
  SendEmailResponse,
  FaqEntry,
  CreateFaqEntryRequest,
  UpdateFaqEntryRequest,
  PurgePreviewResponse,
  PurgeCompanyResponse,
} from '../types/auth';

const API_BASE_URL = config.api.baseUrl;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If response is not JSON, use default error message
    }

    throw new ApiError(errorMessage, response.status);
  }

  return response.json();
};

const getAuthHeaders = (token?: string) => {
  const headers: HeadersInit = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

export const authApi = {
  // Authentication APIs
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.login}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(credentials),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  logout: async (token: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.logout}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
      });

      await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  refreshToken: async (token: string): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.refreshToken}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Company Management APIs
  getAllCompanies: async (token: string): Promise<Company[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.company}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  getCertificationStatus: async (token: string): Promise<any> => {
    try {
      const response = await fetch(`${API_BASE_URL}/company/certificationStatus`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  createCompany: async (token: string, companyData: CreateCompanyRequest): Promise<Company> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.company}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(companyData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  updateCompany: async (token: string, companyId: number, companyData: UpdateCompanyRequest): Promise<Company> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.company}/${companyId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(companyData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  deleteCompany: async (token: string, companyId: number): Promise<{ message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.company}/${companyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });

      const data = await handleApiResponse(response);
      return data as { message: string };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Company Purge APIs (permanent, irreversible delete - admin only)
  getCompanyPurgePreview: async (token: string, companyId: number): Promise<PurgePreviewResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${config.api.endpoints.company}/${companyId}/purge/preview`,
        {
          method: 'GET',
          headers: getAuthHeaders(token),
        }
      );

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  purgeCompany: async (
    token: string,
    companyId: number,
    confirmCompanyId: number
  ): Promise<PurgeCompanyResponse> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${config.api.endpoints.company}/${companyId}/purge`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(token),
          body: JSON.stringify({ confirmCompanyId }),
        }
      );

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // User Management APIs
  getAllUsers: async (token: string): Promise<User[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.user}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  createUser: async (token: string, userData: CreateUserRequest): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.user}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(userData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Create user with default password (combines createUser + verifyUser)
  createUserWithPassword: async (token: string, userData: CreateUserRequest, defaultPassword?: string): Promise<User> => {
    try {
      // First create the user
      const createdUser = await authApi.createUser(token, userData);

      // Then verify the user with password (default is username if not provided)
      const password = defaultPassword || userData.username;
      const verifiedUser = await authApi.verifyUser(createdUser.id, { password });

      return verifiedUser;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  updateUser: async (token: string, userId: number, userData: UpdateUserRequest): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.user}/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(userData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  deleteUser: async (token: string, userId: number): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.user}/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });

      await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  verifyUser: async (userId: number, verifyData: VerifyUserRequest): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.verifyUser}/${userId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(verifyData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Test Suite Management APIs
  getTestSuitesByCompany: async (token: string, companyId: number): Promise<TestSuiteResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.testSuitesByCompany}?companyId=${companyId}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  createTestSuite: async (token: string, testSuiteData: CreateTestSuiteRequest): Promise<CreateTestSuiteResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.createTestSuite}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(testSuiteData),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  getTestDetails: async (token: string, testSuiteInfoId: number): Promise<TestDetails> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.testDetails}?testSuiteInfoId=${testSuiteInfoId}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  updateTest: async (token: string, updateTestData: UpdateTestRequest): Promise<UpdateTestResponse> => {
    try {
      const formData = new FormData();

      formData.append('testSuiteRuleMappingId', updateTestData.testSuiteRuleMappingId.toString());
      formData.append('status', updateTestData.status);

      if (updateTestData.comment !== undefined && updateTestData.comment !== null) {
        formData.append('comment', updateTestData.comment);
      } else {
        formData.append('comment', '');
      }

      if (updateTestData.parentSuiteReceiptEventId !== undefined && updateTestData.parentSuiteReceiptEventId !== null) {
        formData.append('parentSuiteReceiptEventId', updateTestData.parentSuiteReceiptEventId.toString());
      } else {
        formData.append('parentSuiteReceiptEventId', '');
      }

      if (updateTestData.receiptFile) {
        formData.append('receiptFile', updateTestData.receiptFile);
      }

      // Add companyId for admin users to specify which company the test belongs to
      if (updateTestData.companyId !== undefined && updateTestData.companyId !== null) {
        formData.append('companyId', updateTestData.companyId.toString());
      }

      // Add isOptional flag for optional test processing
      if (updateTestData.isOptional !== undefined && updateTestData.isOptional !== null) {
        formData.append('isOptional', updateTestData.isOptional.toString());
      }

      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.updateTest}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*',
        },
        body: formData,
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Upload Receipt API (for initial receipt upload when receipt is null)
  uploadReceipt: async (token: string, uploadData: UploadReceiptRequest): Promise<UploadReceiptResponse> => {
    try {
      const formData = new FormData();

      formData.append('testRuleInfoId', uploadData.testRuleInfoId.toString());
      formData.append('receiptFile', uploadData.receiptFile);

      if (uploadData.comment !== undefined && uploadData.comment !== null) {
        formData.append('comment', uploadData.comment);
      } else {
        formData.append('comment', '');
      }

      // Add companyId for admin users to specify which company the test belongs to
      if (uploadData.companyId !== undefined && uploadData.companyId !== null) {
        formData.append('companyId', uploadData.companyId.toString());
      }

      // Add isOptional flag for optional test processing
      if (uploadData.isOptional !== undefined && uploadData.isOptional !== null) {
        formData.append('isOptional', uploadData.isOptional.toString());
      }

      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.uploadReceipt}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': '*/*',
        },
        body: formData,
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Document Management APIs
  getAllDocuments: async (token: string): Promise<Document[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.documents}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  getGeneralDocuments: async (token: string): Promise<Document[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.documentsGeneral}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  getCompanyDocuments: async (token: string, companyId: number): Promise<Document[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.documentsCompany}/${companyId}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  uploadDocument: async (token: string, documentData: CreateDocumentRequest): Promise<Document> => {
    try {
      const formData = new FormData();
      formData.append('file', documentData.file);
      formData.append('category', documentData.category);

      if (documentData.companyId !== null && documentData.companyId !== undefined) {
        formData.append('companyId', documentData.companyId.toString());
      } else {
        formData.append('companyId', '');
      }

      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.documents}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: formData,
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  deleteDocument: async (token: string, documentId: number): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.documents}/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      });

      await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Notification Management APIs
  getAllNotifications: async (token: string): Promise<Notification[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.notifications}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  getNotificationsByCompany: async (token: string, companyId: number): Promise<Notification[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.notifications}?companyId=${companyId}`, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  markNotificationAsRead: async (token: string, notificationId: number): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.markNotificationRead}/${notificationId}/read`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
      });

      await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Certification APIs
  getCertifications: async (token: string, companyId?: number): Promise<CertificationResponse> => {
    try {
      const url = companyId
        ? `${API_BASE_URL}${config.api.endpoints.certifications}?companyId=${companyId}`
        : `${API_BASE_URL}${config.api.endpoints.certifications}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(token),
      });

      return await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Forgot Password APIs
  forgotPassword: async (request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.forgotPassword}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      });

      return await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  resetPassword: async (request: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.resetPassword}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      });

      return await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Email APIs
  sendEmail: async (token: string, emailData: SendEmailRequest): Promise<SendEmailResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.sendEmail}`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(emailData),
      });

      return await handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Export APIs
  exportActiveUsersCsv: async (token: string): Promise<Blob> => {
    try {
      const response = await fetch(`${API_BASE_URL}${config.api.endpoints.exportActiveUsers}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/csv',
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use default error message
        }

        throw new ApiError(errorMessage, response.status);
      }

      return response.blob();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Network error: Unable to connect to the server. Please check your connection and try again.',
        0
      );
    }
  },

  // Ping Admin APIs
  pingAdmin: async (token: string, testSuiteId: number, message?: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/pingAdmin`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ testSuiteId, message }),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Network error: Unable to ping admin', 0);
    }
  },

  pingAdminBatch: async (token: string, testSuiteIds: number[]): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/pingAdminBatch`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ testSuiteIds }),
      });

      return handleApiResponse(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Network error: Unable to ping admin', 0);
    }
  },

  // FAQ APIs
  getFaqEntries: async (token: string): Promise<FaqEntry[]> => {
    const response = await fetch(`${API_BASE_URL}/faq`, {
      headers: getAuthHeaders(token),
    });
    return handleApiResponse(response);
  },

  createFaqEntry: async (token: string, data: CreateFaqEntryRequest): Promise<FaqEntry> => {
    const response = await fetch(`${API_BASE_URL}/faq`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  updateFaqEntry: async (token: string, id: number, data: UpdateFaqEntryRequest): Promise<FaqEntry> => {
    const response = await fetch(`${API_BASE_URL}/faq/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  deleteFaqEntry: async (token: string, id: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/faq/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}`, response.status);
    }
  },
};
